import {
  CancelIcon,
  CommonPopupCrud,
  FormGroup,
  Grid,
  IconButton,
  MyTextInput,
  Popover,
  textChecker,
  textFieldErr,
  timeoutCall,
  Typography,
} from "@common";
import { addAttachment } from "@features";
import { useFormik } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  buildAcceptString,
  normalizeExtensions,
  validateAttachmentFile,
} from "./utils/attachmentValidation";

const PREFIX = "AddAttachment";

const classes = {
  popoverPaper: `${PREFIX}-popoverPaper`,
};

const validationSchema = Yup.object({
  description: Yup.string()
    .test(
      "noEmptySpaces",
      " Field cannot be only empty spaces",
      function (value) {
        return value === undefined || value.trim().length > 0;
      }
    )
    .max(255, "Description should not exceed more than 255 characters")
    .matches(textChecker, textFieldErr),
});

const AddPage = ({
  showAdd,
  close,
  getFunction,
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forContract,
  forOrder,
  contractId: propContractId,
  orderId: propOrderId,
  allowedFileTypes,
  ...props
}) => {
  const validExtensions = useMemo(
    () => normalizeExtensions(allowedFileTypes || props?.allowedFileTypes),
    [allowedFileTypes, props?.allowedFileTypes]
  );
  const acceptString = useMemo(
    () => buildAcceptString(validExtensions),
    [validExtensions]
  );

  const [selectedFileName, setSelectedFileName] = useState("");
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [specificFileName, setSpecificFileName] = useState("");
  const [key, setKey] = useState(0);

  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const loading = useSelector((state) => state?.attachment?.loading);

  const campaignSelectedData = useSelector(
    (state) => state?.campaign?.selectedData
  );
  const prospectSelectedData = useSelector(
    (state) => state?.prospect?.selectedData
  );
  const inquirySelectedData = useSelector(
    (state) => state?.inquiry?.selectedData
  );
  const leadSelectedData = useSelector((state) => state?.lead?.selectedData);
  const opportunitySelectedData = useSelector(
    (state) => state?.opportunity?.selectedData
  );
  const contractSelectedData = useSelector(
    (state) => state?.contract?.selectedData
  );
  const orderSelectedData = useSelector(
    (state) => state?.order?.selectedData
  );

  const campaignId = forCampaign
    ? campaignSelectedData?.id || props?.campaignId
    : props?.campaignId || null;
  const prospectId = forProspect
    ? prospectSelectedData?.id || props?.prospectId || props?.prospectId
    : props?.prospectId || props?.prospectId || null;
  const inquiryId = forInquiry
    ? inquirySelectedData?.id || props?.inquiryId
    : props?.inquiryId || null;
  const leadId = forLead
    ? leadSelectedData?.id || props?.leadId
    : props?.leadId || null;
  const opportunityId = forOpportunity
    ? opportunitySelectedData?.id || props?.opportunityId
    : props?.opportunityId || null;
  const contractId = forContract
    ? (Array.isArray(contractSelectedData) ? contractSelectedData[0]?.id : contractSelectedData?.id) || propContractId || props?.contractId
    : propContractId || props?.contractId || null;
  const orderId = forOrder
    ? (Array.isArray(orderSelectedData) ? orderSelectedData[0]?.id : orderSelectedData?.id) || propOrderId || props?.orderId
    : propOrderId || props?.orderId || null;
  const organizationId = props?.organizationId || props?.orgId || null;

  const handlePopoverOpen = (event, selectedFileName) => {
    setAnchorEl(event.currentTarget);
    setSpecificFileName(selectedFileName);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const resetUploadState = useCallback((resetForm) => {
    resetForm?.();
    setSelectedFileName("");
    setFile(null);
    setFileErr("");
    setKey((prevKey) => prevKey + 1);
  }, []);

  const formik = useFormik({
    initialValues: {
      description: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
      onSubmit: async (values, { resetForm }) => {
        if (!file) {
          setFileErr("File is Required");
          return false;
        }

        setFileErr("");

        const formData = new FormData();
        if (organizationId) formData.append("organizationId", organizationId);
        if (campaignId) formData.append("campaignId", campaignId);
        if (prospectId) formData.append("prospectId", prospectId);
        if (inquiryId) formData.append("inquiryId", inquiryId);
        if (leadId) formData.append("leadId", leadId);
        if (opportunityId) formData.append("opportunityId", opportunityId);
        if (contractId) formData.append("contractId", contractId);
        if (orderId) formData.append("orderId", orderId);

        formData.append("file", file);
        formData.append("description", values.description || "");

        const res = await dispatch(addAttachment(formData));
        if (
          res?.payload?.data?.ok ||
          res?.payload?.ok ||
          res?.meta?.requestStatus === "fulfilled"
        ) {
          getFunction?.();
          timeoutCall(() => resetUploadState(resetForm));
          return true;
        }
        return false;
      },
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const { isValid, error } = validateAttachmentFile(file, validExtensions);

      if (!isValid) {
        setFileErr(error);
        setSelectedFileName("");
        setFile(null);
      } else {
        setSelectedFileName(file.name);
        setFile(file);
        setFileErr("");
      }
    }

    // Clear the file input
    event.target.value = "";
  };

  const handleClear = () => {
    setSelectedFileName("");
    setFile(null);
    setFileErr("File is Required");
    setKey((prevKey) => prevKey + 1);
  };

  const handleSubmit = useCallback(() => {
    return formik.submitForm();
  }, [formik]);

  return (
    <CommonPopupCrud
      open={showAdd}
      heading="Add Attachment"
      handleSubmit={handleSubmit}
      close={close}
      mediumScreen={true}
      loading={loading}
    >
      <form noValidate onSubmit={formik?.handleSubmit}>
        <Grid container>
          <MyTextInput
            label="Description"
            type="text"
            name="description"
            fullWidth={true}
            onBlur={formik.handleBlur}
            margin="normal"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />
        </Grid>
        <Grid style={{ display: "flex", gap: 50 }}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <FormGroup className="file-browse-btn-div">
              <label className="btn label-sm file-browse-btn">
                Select File *
                <MyTextInput
                  key={key}
                  type="file"
                  name="l1MenuIcon"
                  style={{
                    display: "none",
                    marginTop: "27px",
                    marginLeft: "-28px",
                  }}
                  className="form-control-sm file-input"
                  onChange={handleFileChange}
                  accept={acceptString}
                  inputProps={{
                    accept: acceptString,
                  }}
                  required={true}
                  size="small"
                />
              </label>
            </FormGroup>
          </Grid>
          <Grid
            style={{ display: "flex", alignItems: "center" }}
            size={{
              xs: 12,
              sm: 6,
              md: 8,
            }}
          >

            <p
              onMouseEnter={(event) =>
                handlePopoverOpen(event, selectedFileName)
              }
              onMouseLeave={handlePopoverClose}
            >
              {" "}
              {selectedFileName?.length > 40
                ? selectedFileName.slice(0, 40) + "...."
                : selectedFileName}
            </p>
            {selectedFileName && (
              <IconButton onClick={handleClear}>
                <CancelIcon color="error" />
              </IconButton>
            )}
          </Grid>

          <Popover
            classes={{
              paper: classes.popoverPaper,
            }}
            id="mouse-over-popover"
            sx={{
              pointerEvents: "none",
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Typography sx={{ p: 1 }}> {specificFileName}</Typography>
          </Popover>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 12,
          }}
        >
          {fileErr && <p className="formik_err">{fileErr}</p>}
        </Grid>
      </form>
    </CommonPopupCrud>
  );
};

export default AddPage;
